import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRightLeft } from 'lucide-react';
import {
    reschedulePost, listPosts, getPageConfig,
    type SocialPost,
} from '@/services/api/social-publisher';
import { useToast } from '@/hooks/use-toast';
import { parseBackendDate } from '../dates';


interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    post: SocialPost | null;
    pageConfigId: number;
}

const DAY_NAMES_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DAY_NAMES_LONG = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];


/**
 * Modal de reagendar com grid de slots do horizonte.
 *
 * Layout: colunas = próximos N dias, linhas = horários configurados
 * (post_schedule_times ou story_schedule_times conforme post.type).
 * Cada célula é 1 slot — mostra vazio, ocupado (por outro post) ou é o slot atual.
 * Clique em slot vazio → reagenda. Clique em ocupado → swap.
 */
export default function RescheduleModal({ open, onOpenChange, post, pageConfigId }: Props) {
    const qc = useQueryClient();
    const { toast } = useToast();

    const { data: cfg } = useQuery({
        queryKey: ['social-publisher', 'page-config', pageConfigId],
        queryFn: () => getPageConfig(pageConfigId),
        enabled: open,
    });
    const { data: allScheduled = [] } = useQuery({
        queryKey: ['social-publisher', 'posts', pageConfigId, 'scheduled-map', post?.type],
        queryFn: () => listPosts({
            page_config_id: pageConfigId,
            type: post?.type,
            status: 'queued',
            limit: 500,
        }),
        enabled: open && !!post,
    });

    const kind = post?.type; // 'feed' | 'story'
    const scheduleTimes = useMemo(() => {
        if (!cfg || !kind) return [];
        const raw = kind === 'story'
            ? (cfg.story_schedule_times || [])
            : (cfg.post_schedule_times || []);
        return (raw as string[]).slice().sort();
    }, [cfg, kind]);

    const horizonDays = cfg?.horizon_days || 7;

    // Gera lista de datas (partindo da data BRT de hoje).
    // Extrai ano/mês/dia direto do formatador em BRT pra evitar bugs de timezone.
    const days = useMemo(() => {
        const out: { y: number; mo: number; d: number; label: string; dayNameShort: string; dayNameLong: string; isToday: boolean }[] = [];
        const now = new Date();
        for (let i = 0; i < horizonDays; i++) {
            const dt = new Date(now.getTime() + i * 24 * 3600 * 1000);
            // Componentes em BRT
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'America/Sao_Paulo',
                year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
            }).formatToParts(dt);
            const y = Number(parts.find((p) => p.type === 'year')?.value);
            const mo = Number(parts.find((p) => p.type === 'month')?.value);
            const d = Number(parts.find((p) => p.type === 'day')?.value);
            // Índice do dia da semana (0=Dom) usando UTC noon → sem risco de virar
            const dayOfWeek = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0)).getUTCDay();
            out.push({
                y, mo, d,
                label: `${String(d).padStart(2, '0')}/${String(mo).padStart(2, '0')}`,
                dayNameShort: DAY_NAMES_SHORT[dayOfWeek],
                dayNameLong: DAY_NAMES_LONG[dayOfWeek],
                isToday: i === 0,
            });
        }
        return out;
    }, [horizonDays]);

    // Mapa scheduled_at (utc string normalizado) → post
    // Backend retorna naive UTC — parseBackendDate garante interpretação correta.
    const slotMap = useMemo(() => {
        const m = new Map<string, SocialPost>();
        for (const p of allScheduled) {
            if (!p.scheduled_at || p.scheduled_at.indexOf('9999') >= 0) continue;
            const d = parseBackendDate(p.scheduled_at);
            if (!d) continue;
            m.set(d.toISOString(), p);
        }
        return m;
    }, [allScheduled]);

    const mut = useMutation({
        mutationFn: (isoUtc: string) => reschedulePost(post!.id, isoUtc),
        onSuccess: (res) => {
            toast({ title: 'Reagendado', description: res.message || undefined });
            qc.invalidateQueries({ queryKey: ['social-publisher', 'posts'] });
            onOpenChange(false);
        },
        onError: (e: any) => toast({
            title: 'Erro',
            description: e?.response?.data?.detail || 'Falha',
            variant: 'destructive',
        }),
    });

    if (!post) return null;

    // Constrói o horário UTC pra um (dia BRT, HH:MM local BRT).
    // BRT = UTC-3 (Brasil não tem DST desde 2019).
    const buildUtcSlot = (day: { y: number; mo: number; d: number }, hhmm: string): string => {
        const [h, mi] = hhmm.split(':').map(Number);
        // Constrói UTC = BRT + 3h
        const utcMs = Date.UTC(day.y, day.mo - 1, day.d, h + 3, mi, 0);
        return new Date(utcMs).toISOString();
    };

    const getSlotInfo = (day: { y: number; mo: number; d: number }, hhmm: string) => {
        const utcIso = buildUtcSlot(day, hhmm);
        const occupant = slotMap.get(utcIso);
        const isCurrent = occupant?.id === post.id;
        const isPast = new Date(utcIso).getTime() < Date.now();
        return { utcIso, occupant, isCurrent, isPast };
    };

    const onSlotClick = (utcIso: string, occupant: SocialPost | undefined, isCurrent: boolean, isPast: boolean) => {
        if (isPast || isCurrent) return;
        if (occupant) {
            const ok = confirm(
                `Esse horário já tem o post #${occupant.id}. Fazer swap? ` +
                `(o post #${occupant.id} vai receber o horário atual do #${post.id})`
            );
            if (!ok) return;
        }
        mut.mutate(utcIso);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[900px] p-0 border-none rounded-2xl overflow-hidden">
                <div className="bg-white">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                        <div className="bg-amber-100 p-2 rounded-lg">
                            <Calendar className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-800">
                                Reagendar post #{post.id}
                            </h3>
                            <p className="text-xs text-gray-500 truncate">
                                {post.type === 'story' ? 'Story' : 'Feed'} · Clique num slot vazio pra mover.
                                Se já tiver outro post, faz troca.
                            </p>
                        </div>
                    </div>

                    <div className="p-4 max-h-[70vh] overflow-auto">
                        {scheduleTimes.length === 0 ? (
                            <div className="text-center py-8 text-sm text-gray-500">
                                Nenhum horário configurado pra {post.type} nessa página.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs border-separate border-spacing-1">
                                    <thead>
                                        <tr>
                                            <th className="text-left px-2 py-1 text-gray-500 font-normal sticky left-0 bg-white z-10">
                                                Horário
                                            </th>
                                            {days.map((d, idx) => (
                                                <th key={idx} className={`text-center px-2 py-1 font-normal ${
                                                    d.isToday ? 'text-purple-700 font-semibold' : 'text-gray-500'
                                                }`}>
                                                    <div>{d.dayNameShort}</div>
                                                    <div className="text-[10px] font-normal">{d.label}</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scheduleTimes.map((hhmm) => (
                                            <tr key={hhmm}>
                                                <td className="px-2 py-1 text-gray-700 font-medium sticky left-0 bg-white z-10">
                                                    {hhmm}
                                                </td>
                                                {days.map((d, idx) => {
                                                    const { utcIso, occupant, isCurrent, isPast } = getSlotInfo(d, hhmm);
                                                    let cls = 'text-center rounded-md p-1.5 min-h-[52px] transition-colors cursor-pointer';
                                                    let content: React.ReactNode = <span className="text-gray-300">livre</span>;
                                                    if (isPast) {
                                                        cls += ' bg-gray-50 text-gray-300 cursor-not-allowed';
                                                        content = <span className="text-[10px]">passou</span>;
                                                    } else if (isCurrent) {
                                                        cls += ' bg-purple-100 border-2 border-purple-500 cursor-default';
                                                        content = (
                                                            <>
                                                                <div className="text-purple-700 font-semibold">✓ Atual</div>
                                                                <div className="text-[10px] text-purple-600">#{post.id}</div>
                                                            </>
                                                        );
                                                    } else if (occupant) {
                                                        cls += ' bg-blue-50 border border-blue-200 hover:bg-blue-100 text-blue-700';
                                                        content = (
                                                            <>
                                                                <ArrowRightLeft className="w-3 h-3 mx-auto mb-0.5" />
                                                                <div className="text-[10px] truncate">#{occupant.id}</div>
                                                            </>
                                                        );
                                                    } else {
                                                        cls += ' bg-white border border-gray-200 hover:border-purple-400 hover:bg-purple-50';
                                                    }
                                                    return (
                                                        <td key={idx}
                                                            className={cls}
                                                            onClick={() => onSlotClick(utcIso, occupant, isCurrent, isPast)}
                                                            title={isCurrent ? 'Horário atual' : (occupant ? `Trocar com #${occupant.id}` : 'Mover pra cá')}
                                                        >
                                                            {content}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex items-center gap-1">
                                <span className="w-3 h-3 rounded bg-purple-100 border-2 border-purple-500"></span>
                                atual
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="w-3 h-3 rounded bg-blue-50 border border-blue-200"></span>
                                swap
                            </span>
                            <span className="inline-flex items-center gap-1">
                                <span className="w-3 h-3 rounded bg-white border border-gray-200"></span>
                                livre
                            </span>
                        </div>
                        <Button variant="ghost" onClick={() => onOpenChange(false)}>
                            Fechar
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
